import {
  MEASUREMENT_REQUEST_TYPE_IDS, PRODUCT_IDS,
  MULTIFAMILY,
  RESIDENTIAL,
  CURRENCY_SYMBOL,
  NEW_UNIFIED_SKUS,
} from 'constants/AppConstants';
import {
  ENTITY_TYPES, ADDRESS_MAPPER, LOADING_ADDRESS, ERROR_LOADING_ADDRESS,
} from 'features/placeOrder/OrderAddresses.constants';

export const getProductFromId = (list, id) => list?.find((product) => product.ProductID === id);

export const getPrimarySubscriptionProductsFromUserProducts = (userProducts) => {
  const activeProductIds = new Set();
  const productByProductId = {};
  if (!userProducts || typeof userProducts !== 'object') return { activeProductIds, productByProductId };
  const structureKeys = Object.keys(userProducts).filter((k) => userProducts[k]?.PrimaryProducts);
  structureKeys.forEach((key) => {
    (userProducts[key].PrimaryProducts || []).forEach((p) => {
      const id = p.ProductID;
      activeProductIds.add(id);
      if (!productByProductId[id]) {
        productByProductId[id] = {
          PriceMin: p.PriceMin,
          PriceMax: p.PriceMax,
          Name: p.Name ?? '',
          WebDeliveryProducts: p.WebDeliveryProducts ?? [],
          CompatibleProducts: p.CompatibleProducts ?? [],
        };
      }
    });
  });
  return { activeProductIds, productByProductId };
};

/** Get full primary product object from userProducts by ProductID (for subscription flow → Redux) */
export const getPrimaryProductByIdFromUserProducts = (userProducts, productId) => {
  if (!userProducts || typeof userProducts !== 'object') return null;
  const structureKeys = Object.keys(userProducts).filter((k) => userProducts[k]?.PrimaryProducts);
  for (let i = 0; i < structureKeys.length; i += 1) {
    const list = userProducts[structureKeys[i]].PrimaryProducts || [];
    const found = list.find((p) => p.ProductID === productId);
    if (found) return found;
  }
  return null;
};

export const getAdjustedProductPrice = (product, selectedPrimaryProduct = null, isESXEnabled = true) => {
  const priceMultiplier = isESXEnabled
    && product.ProductID === PRODUCT_IDS.ESX
    && selectedPrimaryProduct?.ProductID === PRODUCT_IDS.Full_House
    ? 2
    : 1;

  return {
    priceMin: (product.PriceMin || 0) * priceMultiplier,
    priceMax: (product.PriceMax || 0) * priceMultiplier,
  };
};

export const addProductPriceToEstimate = (
  estimateMin,
  estimateMax,
  product,
  deliveryProductId,
  bundleDiscountList,
  selectedPrimaryProduct = null,
  isESXEnabled = true,
) => {
  const delivery = getProductFromId(product.WebDeliveryProducts, deliveryProductId) || {};
  let { priceMin: productPriceMin, priceMax: productPriceMax } = getAdjustedProductPrice(product, selectedPrimaryProduct, isESXEnabled);
  const deliveryPriceMin = (delivery.PriceMin ? delivery.PriceMin : 0);
  const deliveryPriceMax = (delivery.PriceMax ? delivery.PriceMax : 0);
  const bundleDiscount = bundleDiscountList.find(({ BundleProduct }) => BundleProduct === product.ProductID);
  // #genai optional chaining, discount objects are nullish or populated
  if (bundleDiscount?.IsDiscountPercentage) {
    productPriceMin *= ((100 - bundleDiscount.Value) / 100);
    productPriceMax *= ((100 - bundleDiscount.Value) / 100);
  }
  if (bundleDiscount && !bundleDiscount.IsDiscountPercentage) {
    productPriceMin -= bundleDiscount.Value;
    productPriceMax -= bundleDiscount.Value;
  }
  return [
    estimateMin + productPriceMin + deliveryPriceMin,
    estimateMax + productPriceMax + deliveryPriceMax,
  ];
};

export function getCurrencySymbol(currencyId) {
  return CURRENCY_SYMBOL[currencyId] || '$';
}

export function getFormatedDeliveryOption(name, price, currencyId, productId = null) {
  // For Property Data (ID: 112), show "Instant" instead of "Regular"
  const displayName = (productId === PRODUCT_IDS.PropertyData && name === 'Regular') ? 'Instant' : name;
  return `${displayName} ${getCurrencySymbol(currencyId)}${price}`;
}

export const getEstimatedTotalText = (total, currencyTypeId, numberOfPins = 1) => {
  const currency = getCurrencySymbol(currencyTypeId);
  const [min, max] = total;
  if (min === max) return `${currency}${min * numberOfPins}`;
  return `${currency}${min * numberOfPins} - ${currency}${max * numberOfPins}`;
};

export const initBuidingDetails = ({ buildingId, details }) => ({
  buildingId,
  address: details.addressline1,
  city: details.locality,
  state: details.adminLevel1,
  postalcode: details.postalcode,
});

// #genai - Next pin label must follow max existing building ID (not pin count),
// so editing e.g. 4→5 makes the next drop 6 instead of another 5.
export const getNextBuildingId = (mapPoints = []) => {
  const usedIds = mapPoints.map(({ building }, index) => building || String(index + 1));
  // #genai Number ignores the extra index/array arguments supplied by Array#map.
  const numericIds = usedIds
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);
  const maxId = numericIds.length > 0 ? Math.max(...numericIds) : mapPoints.length;
  let nextId = maxId + 1;
  const usedSet = new Set(usedIds);
  while (usedSet.has(String(nextId))) {
    nextId += 1;
  }
  return String(nextId);
};

export const formatAddress = ({
  address, city, state, zip,
}) => `${address}, ${city}, ${state}, ${zip}`;

// True when the pin's address is the placeholder dispatched while the geocode request is in flight.
export const isMapPointAddressLoading = (mapPoint) => (
  mapPoint?.address?.address === LOADING_ADDRESS
);

// True when the pin's address is the placeholder dispatched after a failed geocode (user must retry).
export const isMapPointAddressErrored = (mapPoint) => (
  mapPoint?.address?.address === ERROR_LOADING_ADDRESS
);

// True only when the geocode resolved to a record that is missing a required field for place-order.
// Returns false when there is no address yet (e.g. fresh pin) or when loading/errored — those are
// reported by isMapPointAddressLoading / isMapPointAddressErrored so callers can distinguish them.
// Defensive against malformed shapes (e.g. address as a string instead of object): we only treat
// the address as "incomplete" when we can actually destructure the expected fields off of it.
export const isMapPointAddressIncomplete = (mapPoint) => {
  const addr = mapPoint?.address;
  if (!addr || typeof addr !== 'object') return false;
  if (isMapPointAddressLoading(mapPoint) || isMapPointAddressErrored(mapPoint)) return false;
  const { address, city, zip } = addr;
  return !address || !city || !zip;
};

// Aggregate guard used by the Order page Next button: every pin must be fully resolved and complete.
export const areAllMapPointsReadyForPlaceOrder = (mapPoints) => {
  if (!Array.isArray(mapPoints) || mapPoints.length === 0) return true;
  return mapPoints.every((mapPoint) => (
    !isMapPointAddressLoading(mapPoint)
    && !isMapPointAddressErrored(mapPoint)
    && !isMapPointAddressIncomplete(mapPoint)
  ));
};

export const filterMeasurementRequestTypes = (propertyType, selectedProductId, isPropertyDataSelected = false) => ({ id }) => {
  // If Property Data is selected, show only "Other (please provide instructions)"
  if (isPropertyDataSelected) {
    return id === MEASUREMENT_REQUEST_TYPE_IDS.Other;
  }

  if (propertyType === RESIDENTIAL && id === MEASUREMENT_REQUEST_TYPE_IDS.CommercialComplex) return false;
  if (NEW_UNIFIED_SKUS.includes(selectedProductId)) {
    return (MEASUREMENT_REQUEST_TYPE_IDS.AllStructuresOnParcel === id);
  }
  if (propertyType === MULTIFAMILY) {
    return ([MEASUREMENT_REQUEST_TYPE_IDS.PrimaryStructureOnly,
      MEASUREMENT_REQUEST_TYPE_IDS.CommercialComplex,
      MEASUREMENT_REQUEST_TYPE_IDS.Other].includes(id));
  }
  if ([PRODUCT_IDS.QuickSquaresResidential, PRODUCT_IDS.BidPerfect, PRODUCT_IDS.GutterResidential].includes(selectedProductId)) {
    return ([MEASUREMENT_REQUEST_TYPE_IDS.PrimaryPlusDetachedGarage, MEASUREMENT_REQUEST_TYPE_IDS.PrimaryStructureOnly].includes(id));
  }
  if ([PRODUCT_IDS.QuickSquaresCommercial, PRODUCT_IDS.GutterCommercial].includes(selectedProductId)) {
    return (MEASUREMENT_REQUEST_TYPE_IDS.PrimaryStructureOnly === id);
  }

  return true;
};

export const getDefaultMeasurementRequestTypes = (selectedProductId) => {
  if ([PRODUCT_IDS.QuickSquaresResidential, PRODUCT_IDS.BidPerfect, PRODUCT_IDS.GutterResidential].includes(selectedProductId)) {
    return { selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.PrimaryPlusDetachedGarage };
  }
  if ([PRODUCT_IDS.QuickSquaresCommercial, PRODUCT_IDS.GutterCommercial].includes(selectedProductId)) {
    return { selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.PrimaryStructureOnly };
  }
  if (NEW_UNIFIED_SKUS.includes(selectedProductId)) {
    return { selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.AllStructuresOnParcel };
  }
  if (selectedProductId === PRODUCT_IDS.PropertyData) {
    return { selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.Other };
  }
  return {};
};

/**
 * Utility function to check if Property Data product is selected
 * @param {Object} selectedPrimaryProduct - The selected primary product
 * @returns {boolean} - True if Property Data is selected, false otherwise
 */
export const isPropertyDataSelected = (selectedPrimaryProduct) => (
  // Check if Property Data is selected as primary product
  Boolean(selectedPrimaryProduct?.ProductID === PRODUCT_IDS.PropertyData)
);

export const hasBlueprintProduct = (userProducts) => Boolean(
  userProducts[RESIDENTIAL]?.PrimaryProducts?.find(({ ProductID }) => ProductID === PRODUCT_IDS.BlueprintResidential),
);

export const getDuplicateReportsColumns = (t, classes) => [
  { label: t('order_submitted__report_number'), dataKey: 'reportId' },
  { label: t('common__product'), dataKey: 'product' },
  { label: t('order_submitted__address'), dataKey: 'address' },
  { label: t('common__date'), dataKey: 'date' },
  { label: t('order_details__claim'), dataKey: 'claimNumber' },
  { label: t('common_delivery'), dataKey: 'delivery' },
  { label: t('common__status'), dataKey: 'status' },
  { label: '', dataKey: 'actions', cellProps: { className: classes.actionsCell } },
];

export const getGeocodedAddressFromAddressResponse = (addressResponse) => {
  const {
    address,
    details = {},
    lat,
    lon,
  } = addressResponse || {};
  const {
    addressline1,
    addressline2,
    adminLevel1,
    adminLevel2,
    country,
    locality,
    postalcode,
    entityType = '',
  } = details;

  const entity = (entityType || '').toLowerCase();

  return {
    address: addressline1 || address,
    address2: addressline2,
    addressTypeId: null,
    city: locality,
    country,
    county: adminLevel2,
    latitude: lat,
    longitude: lon,
    mapperUsedId: ADDRESS_MAPPER.EGS,
    state: adminLevel1,
    verificationResultTypeId: (entity === ENTITY_TYPES.Parcel
    || entity === ENTITY_TYPES.Structure) ? 0 : 2,
    verifierUsedId: ADDRESS_MAPPER.EGS,
    zip: postalcode,
  };
};

/**
 * Pull a US or CA postal code from free-text address input.
 * Used when street-level geocoding fails so the map can center on the zip instead.
 */
export const extractZipCodeFromText = (text = '', country = 'US') => {
  if (!text || typeof text !== 'string') return '';
  if (country === 'CA') {
    const match = /\b[A-Z]\d[A-Z][ -]?\d[A-Z]\d\b/.exec(text.toUpperCase());
    return match ? match[0].replaceAll(/\s+/g, ' ') : '';
  }
  const normalize = (zip) => zip.replace(/\s+/, '-');
  // Prefer a zip at the end of the string (after city/state). Never use a leading
  // 5-digit house number as the zip.
  const trailing = /\b(\d{5}(?:[-\s]\d{4})?)\s*$/.exec(text);
  if (trailing) return normalize(trailing[1]);
  const afterState = /\b[A-Za-z]{2}[,\s]+(\d{5}(?:[-\s]\d{4})?)\b/.exec(text);
  if (afterState) return normalize(afterState[1]);
  return '';
};

/**
 * True when stored lat/lon cannot center a usable map (missing, NaN, or null-island 0,0).
 * Report APIs often return latitude/longitude as 0 when geocoding failed at order time.
 */
export const hasInvalidMapCoordinates = (address) => {
  if (!address) return true;
  const lat = Number(address.latitude);
  const lon = Number(address.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return true;
  return lat === 0 && lon === 0;
};

/** True when a geocode response lat/lon can actually center a map (rejects null and 0,0). */
export const hasUsableGeocodeCoordinates = (lat, lon) => (
  lat != null && lon != null && !hasInvalidMapCoordinates({ latitude: lat, longitude: lon })
);

/**
 * True when the forward-geocode result cannot place a street or structure pin.
 * Keep any street / parcel / structure match even if confidence is low — zip fallback
 * must not overwrite a usable building pin with a zip centroid.
 */
export const isGeocodeUnresolvable = (addressResponse) => {
  if (!hasUsableGeocodeCoordinates(addressResponse?.lat, addressResponse?.lon)) return true;

  const details = addressResponse?.details;
  if (!details) return true;

  const entity = (details.entityType || '').toLowerCase();
  if (entity === ENTITY_TYPES.Parcel || entity === ENTITY_TYPES.Structure) return false;
  // Postcode-only hits are zip centroids, not structure pins — treat as unresolvable
  // so the saga can set usedZipFallback and use zip-area zoom.
  if (entity.includes('postcode') || entity.includes('postal')) return true;

  const hasStreetDetail = Boolean(details.addressline1 || details.street || details.streetNumber);
  return !hasStreetDetail;
};

/**
 * Detect stored report addresses that cannot drive a usable structure-level map.
 * Only invalid/missing coords trigger zip fallback — do not overwrite a usable pin
 * just because city/zip or PLSS text is incomplete.
 */
export const isStoredAddressUnresolvable = (address) => {
  if (!address) return true;
  return hasInvalidMapCoordinates(address);
};

/**
 * Build the map pin address when street geocode fails but zip geocode succeeds.
 * Keeps the user's original address text; uses zip lat/lon for map centering.
 */
export const buildZipFallbackGeocodedAddress = ({
  zipGeocodeResponse,
  originalAddressText,
  originalUserEnteredAddress,
  extractedZip,
}) => {
  const zipGeocoded = getGeocodedAddressFromAddressResponse(zipGeocodeResponse);
  return {
    ...zipGeocoded,
    address: originalAddressText || zipGeocoded.address,
    zip: extractedZip || zipGeocoded.zip,
    usedZipFallback: true,
    // Prefer city/state from zip geocode when the original geocode left them empty
    city: zipGeocoded.city || originalUserEnteredAddress?.city || '',
    state: zipGeocoded.state || originalUserEnteredAddress?.state || '',
  };
};

/** Remove UI-only fields so they are not spread into place-order / update-address APIs. */
export const stripUiAddressFlags = (address) => {
  if (!address || typeof address !== 'object') return address;
  const { usedZipFallback, ...apiAddress } = address;
  return apiAddress;
};

/**
 * Zip-fallback is a map-point hint (like userMovedThePin), never an address field.
 * Address edits / reverse geocode must not add or remove it.
 */
export const createMapPointFromGeocode = (geocodedAddress, extras = {}) => ({
  address: stripUiAddressFlags(geocodedAddress),
  userMovedThePin: false,
  userEditedAddress: false,
  usedZipFallback: Boolean(geocodedAddress?.usedZipFallback),
  ...extras,
});
